import { useEffect, useState } from "react";
import axios from "axios";
import { FileText, Lock, Link2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function DeckGate({ deck, onClose }) {
  const [form, setForm] = useState({ name: "", firm: "", mobile: "", email: "", password: "" });
  const [viewer, setViewer] = useState(null); // { source_type, url }
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.firm.trim() || !form.mobile.trim()) {
      toast.error("Name, firm and mobile number are required");
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/decks/${deck.id}/access`, form);
      if (res.data.source_type === "link") {
        window.open(res.data.url, "_blank", "noopener");
        setViewer({ source_type: "link", url: res.data.url });
      } else {
        setViewer({ source_type: "pdf", url: `${BACKEND}${res.data.url}` });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open deck");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="survey-modal-overlay" onClick={onClose} data-testid="deck-gate-overlay">
      <div className="survey-modal deck-viewer-modal" onClick={(e) => e.stopPropagation()} data-testid="deck-gate-modal">
        <div className="survey-modal-bar">
          <strong>{deck.title}</strong>
          <button onClick={onClose} data-testid="deck-gate-close"><X size={18} /></button>
        </div>
        {!viewer ? (
          <form className="deck-gate-form" onSubmit={submit} data-testid="deck-gate-form">
            <p className="deck-gate-note">Tell us who you are to open this deck.</p>
            <label>Full name *</label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="deck-gate-name" />
            <label>Firm name *</label>
            <Input value={form.firm} onChange={(e) => set("firm", e.target.value)} data-testid="deck-gate-firm" />
            <label>Mobile number *</label>
            <Input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} data-testid="deck-gate-mobile" />
            <label>Email (optional)</label>
            <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="deck-gate-email" />
            {deck.has_password && (
              <>
                <label>Access password *</label>
                <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="deck-gate-password" />
              </>
            )}
            <Button type="submit" className="primary-cta full" disabled={busy} data-testid="deck-gate-submit">
              {busy ? "Opening…" : "Open deck"}
            </Button>
          </form>
        ) : viewer.source_type === "pdf" ? (
          <iframe title={deck.title} src={viewer.url} className="deck-pdf-frame" data-testid="deck-pdf-frame" />
        ) : (
          <div className="deck-link-open" data-testid="deck-link-open">
            <p>The deck opened in a new tab.</p>
            <a href={viewer.url} target="_blank" rel="noreferrer" className="primary-cta">Open again <Link2 size={15} /></a>
          </div>
        )}
      </div>
    </div>
  );
}

export function PitchVault() {
  const [decks, setDecks] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios.get(`${API}/decks`).then((r) => setDecks(r.data || [])).catch(() => {});
  }, []);

  return (
    <section id="vault" className="dark-section pitch-vault" data-testid="pitch-vault-section">
      <div className="section-head reveal">
        <span className="eyebrow-dark"><FileText size={13} /> Pitch deck vault</span>
        <h2>For investors and partners.</h2>
        <p>Request access to our latest decks. Every open is logged.</p>
      </div>
      {!decks.length ? (
        <p className="survey-empty" data-testid="vault-empty">No decks are published yet.</p>
      ) : (
        <div className="vault-grid" data-testid="vault-grid">
          {decks.map((d) => (
            <article key={d.id} className="vault-card reveal" data-testid={`vault-card-${d.id}`}>
              <span className="vault-icon">{d.source_type === "link" ? <Link2 size={20} /> : <FileText size={20} />}</span>
              <h3 data-testid={`vault-title-${d.id}`}>{d.title}</h3>
              {d.description && <p>{d.description}</p>}
              <div className="vault-card-foot">
                {d.has_password && <span className="vault-locked" data-testid={`vault-locked-${d.id}`}><Lock size={12} /> Password</span>}
                <button className="primary-cta" onClick={() => setActive(d)} data-testid={`vault-open-${d.id}`}>Request access</button>
              </div>
            </article>
          ))}
        </div>
      )}
      {active && <DeckGate deck={active} onClose={() => setActive(null)} />}
    </section>
  );
}
